// Feedback.js
// Wigley's product collaboration workspace — bug/feature/improvement queue,
// curated public roadmap, attachment-aware. Replaces the FeatureBase tool.
//
// Three tabs:
//   1. Submissions   — the queue, filterable/sortable, click-to-expand
//   2. New           — the submission form (also opened by the floating Report button)
//   3. Roadmap       — read-only for everyone; edit mode for curators
//
// Curators (ntrg_staff.is_feedback_curator = true) can:
//   - Move submissions between statuses
//   - Edit/delete submissions
//   - Add/edit/reorder roadmap items
//
// Anyone can:
//   - Submit new feedback
//   - View the queue
//   - Read the roadmap
//   - Open attachments

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { uploadFile } from '../utils/uploadFile';
import { keysDelete } from '../utils/keysDelete';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPES = ['Bug', 'Feature', 'Improvement'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const STATUSES = ['Inbox', 'Triaged', 'In Progress', 'In Focus', 'Done', "Won't Do"];
const STATUSES_OPEN = ['Inbox', 'Triaged', 'In Progress', 'In Focus'];

const SECTIONS = [
  '— Unassigned —',
  'Consultant Dashboard',
  'Document Intake',
  'Notice Audit',
  'Documents',
  'NTRG Dashboards',
  'Clients Screen',
  'Properties',
  'Parcels Screen',
  'Consultants Screen',
  'Reports & Activity Screen',
  'File Exchange',
  'Document Scanner',
  'Manage Users Screen',
  'Notice Extractor Screen',
  'Admin Console Screen',
  'Task Engine Screen',
  'Login Screen',
  'Feedback',
  'Other',
];

const ROADMAP_STATUSES = ['Coming Soon', 'In Development', 'Complete'];

// Color tokens (RGB triplets — apply via rgba(${rgb}, alpha))
const TYPE_COLORS = {
  'Bug':         '239,68,68',   // red
  'Feature':     '52,211,153',  // green
  'Improvement': '251,191,36',  // amber
};

const PRIORITY_COLORS = {
  'Critical': '239,68,68',
  'High':     '251,191,36',
  'Medium':   '96,165,250',
  'Low':      '148,163,184',
};

const STATUS_COLORS = {
  'Inbox':       '148,163,184',  // slate
  'Triaged':     '96,165,250',   // blue
  'In Progress': '251,191,36',   // amber
  'In Focus':    '249,115,22',   // orange (FeatureBase coral)
  'Done':        '52,211,153',   // green
  "Won't Do":    '107,114,128',  // muted gray
};

const ROADMAP_STATUS_COLORS = {
  'Complete':       '52,211,153',
  'In Development': '251,191,36',
  'Coming Soon':    '96,165,250',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Feedback({ darkMode, staffUser, initialPrefill = null, onClearPrefill }) {
  const isDark = !!darkMode;
  const isCurator = !!staffUser?.is_feedback_curator;

  const c = {
    bg:         isDark ? '#0f1729' : 'var(--bg)',
    card:       isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.1)'  : 'var(--line)',
    text:       isDark ? '#e8eaf2' : 'var(--ink)',
    sub:        isDark ? '#a8b0cc' : '#4a5068',
    muted:      isDark ? '#8892aa' : 'var(--slate)',
    gold:       'var(--red)',
    inputBg:    isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    inputBorder:isDark ? 'rgba(255,255,255,0.15)' : 'var(--line)',
    rowBg:      isDark ? 'rgba(255,255,255,0.02)' : '#fafbfd',
  };

  const s = {
    label:     { fontSize:'10px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input:     { width:'100%', padding:'8px 11px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'13px', fontFamily:"'Public Sans',system-ui,sans-serif" },
    textarea:  { width:'100%', padding:'8px 11px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'13px', fontFamily:"'Public Sans',system-ui,sans-serif", resize:'vertical', minHeight:'90px' },
    select:    { width:'100%', padding:'8px 11px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'13px' },
    btn:       { padding:'8px 16px', borderRadius:'7px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnPrimary:{ background:c.gold, color:'#fff' },
    btnGhost:  (isDark) => ({ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color:c.sub }),
    badge:     (rgb) => ({ display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius:'10px', background:`rgba(${rgb},0.15)`, color:`rgb(${rgb})`, fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap' }),
    pill:      (rgb, active) => ({ display:'inline-flex', alignItems:'center', padding:'4px 12px', borderRadius:'12px', background: active ? `rgba(${rgb},0.18)` : 'transparent', color: active ? `rgb(${rgb})` : c.sub, fontSize:'12px', fontWeight: active ? '600' : '500', cursor:'pointer', border: active ? `1px solid rgba(${rgb},0.3)` : '1px solid transparent' }),
    kpi:       (rgb, n) => ({ background: n>0 ? `rgba(${rgb},0.08)` : c.rowBg, border:`1px solid ${n>0 ? `rgba(${rgb},0.2)` : c.cardBorder}`, borderRadius:'8px', padding:'12px', textAlign:'center', cursor:'pointer' }),
  };

  // ── Tab state ───────────────────────────────────────────────
  const [tab, setTab] = useState(initialPrefill ? 'new' : 'submissions');

  // Open New tab when a prefill arrives (from floating button)
  useEffect(() => {
    if (initialPrefill) setTab('new');
  }, [initialPrefill]);

  return (
    <div style={{ width:'100%', maxWidth:'1280px', margin:'0 auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom:'18px' }}>
        <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'4px' }}>Feedback</div>
        <div style={{ fontSize:'13px', color:c.muted }}>
          Submit bugs, features, and improvements. Attach sketches and specs. Track what's shipping.
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'18px', borderBottom:`1px solid ${c.cardBorder}` }}>
        <TabBtn active={tab==='submissions'} onClick={()=>setTab('submissions')} c={c}>📋 Submissions</TabBtn>
        <TabBtn active={tab==='new'}         onClick={()=>setTab('new')}         c={c}>＋ New Submission</TabBtn>
        <TabBtn active={tab==='roadmap'}     onClick={()=>setTab('roadmap')}     c={c}>🗺 Roadmap</TabBtn>
      </div>

      {tab === 'submissions' && <SubmissionsTab c={c} s={s} isDark={isDark} isCurator={isCurator} staffUser={staffUser} />}
      {tab === 'new'         && <NewSubmissionTab c={c} s={s} isDark={isDark} staffUser={staffUser} prefill={initialPrefill} onSubmitted={() => { if (onClearPrefill) onClearPrefill(); setTab('submissions'); }} onCancel={() => { if (onClearPrefill) onClearPrefill(); setTab('submissions'); }} />}
      {tab === 'roadmap'     && <RoadmapTab c={c} s={s} isDark={isDark} isCurator={isCurator} staffUser={staffUser} />}
    </div>
  );
}

function TabBtn({ active, onClick, c, children }) {
  return (
    <button onClick={onClick} style={{
      padding:'9px 16px', background:'transparent', color: active ? c.gold : c.sub,
      border:'none', borderBottom: active ? `2px solid ${c.gold}` : '2px solid transparent',
      cursor:'pointer', fontSize:'13px', fontWeight: active ? '700' : '500',
      fontFamily:"'Public Sans',system-ui,sans-serif", marginBottom:'-1px',
    }}>{children}</button>
  );
}

// ---------------------------------------------------------------------------
// Submissions tab
// ---------------------------------------------------------------------------

function SubmissionsTab({ c, s, isDark, isCurator, staffUser }) {
  const [rows, setRows] = useState([]);
  const [attachmentsByFeedback, setAttachmentsByFeedback] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // '' = all
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'all' | <single status>
  const [sortBy, setSortBy] = useState('date'); // date | priority | section | submitter | type | title

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: e1 } = await supabase
        .from('wigley_feedback')
        .select('*')
        .order('created_at', { ascending: false });
      if (e1) throw e1;
      setRows(data || []);

      const ids = (data || []).map(r => r.id);
      if (ids.length) {
        const { data: atts } = await supabase
          .from('wigley_feedback_attachments')
          .select('*')
          .in('feedback_id', ids)
          .order('display_order', { ascending: true });
        const grouped = {};
        (atts || []).forEach(a => {
          (grouped[a.feedback_id] = grouped[a.feedback_id] || []).push(a);
        });
        setAttachmentsByFeedback(grouped);
      } else {
        setAttachmentsByFeedback({});
      }
    } catch (e) {
      setError(e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Counts
  const counts = useMemo(() => {
    const out = { Inbox:0, Triaged:0, 'In Progress':0, 'In Focus':0, Done:0, "Won't Do":0 };
    rows.forEach(r => { out[r.status] = (out[r.status] || 0) + 1; });
    return out;
  }, [rows]);

  // Filtered + sorted view
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let out = rows.filter(r => {
      if (typeFilter && r.type !== typeFilter) return false;
      if (priorityFilter && r.priority !== priorityFilter) return false;
      if (sectionFilter && r.section !== sectionFilter) return false;
      if (statusFilter === 'open' && !STATUSES_OPEN.includes(r.status)) return false;
      else if (statusFilter !== 'open' && statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q) {
        const hay = `${r.title} ${r.description||''} ${r.submitter_name||''} ${r.section||''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const priorityRank = { 'Critical':0, 'High':1, 'Medium':2, 'Low':3 };
    const cmp = {
      date:      (a,b) => new Date(b.created_at) - new Date(a.created_at),
      priority:  (a,b) => (priorityRank[a.priority]??9) - (priorityRank[b.priority]??9),
      section:   (a,b) => (a.section||'').localeCompare(b.section||''),
      submitter: (a,b) => (a.submitter_name||'').localeCompare(b.submitter_name||''),
      type:      (a,b) => (a.type||'').localeCompare(b.type||''),
      title:     (a,b) => (a.title||'').localeCompare(b.title||''),
    };
    out = out.slice().sort(cmp[sortBy] || cmp.date);
    return out;
  }, [rows, search, typeFilter, priorityFilter, sectionFilter, statusFilter, sortBy]);

  async function handleStatusChange(row, newStatus) {
    if (!isCurator) return;
    try {
      const update = { status: newStatus };
      if (newStatus === 'Triaged' && !row.triaged_at) {
        update.triaged_at = new Date().toISOString();
        update.triaged_by_id = staffUser?.id || null;
      }
      const { error: e1 } = await supabase.from('wigley_feedback').update(update).eq('id', row.id);
      if (e1) throw e1;
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...update } : r));
    } catch (e) {
      alert('Status change failed: ' + (e.message || e));
    }
  }

  async function handleDelete(row) {
    if (!isCurator) return;
    if (!window.confirm(`Delete "${row.title}"? This will also remove its attachments.`)) return;
    try {
      // Storage cleanup: remove all attachment files first
      const atts = attachmentsByFeedback[row.id] || [];
      if (atts.length) {
        await keysDelete(atts.map(a => a.storage_path));   // R2: feedback attachments moved to R2 with the upload swap
      }
      // Cascade deletes the attachment rows
      const { error: e1 } = await supabase.from('wigley_feedback').delete().eq('id', row.id);
      if (e1) throw e1;
      setRows(prev => prev.filter(r => r.id !== row.id));
      setAttachmentsByFeedback(prev => { const next = {...prev}; delete next[row.id]; return next; });
    } catch (e) {
      alert('Delete failed: ' + (e.message || e));
    }
  }

  return (
    <div>
      {/* KPI tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:'8px', marginBottom:'16px' }}>
        {STATUSES.map(st => (
          <div key={st} onClick={() => setStatusFilter(statusFilter===st ? 'all' : st)} style={{ ...s.kpi(STATUS_COLORS[st], counts[st]||0), cursor:'pointer', borderWidth: statusFilter===st ? '2px' : '1px' }}>
            <div style={{ fontSize:'20px', fontWeight:'700', color:`rgb(${STATUS_COLORS[st]})` }}>{counts[st] || 0}</div>
            <div style={{ fontSize:'10px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:'2px' }}>{st}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 130px 130px 180px 120px 130px', gap:'8px', marginBottom:'14px', alignItems:'end' }}>
        <div>
          <div style={s.label}>Search</div>
          <input style={s.input} placeholder="title, description, submitter, section..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <div>
          <div style={s.label}>Type</div>
          <select style={s.select} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
            <option value="">All</option>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Priority</div>
          <select style={s.select} value={priorityFilter} onChange={e=>setPriorityFilter(e.target.value)}>
            <option value="">All</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Section</div>
          <select style={s.select} value={sectionFilter} onChange={e=>setSectionFilter(e.target.value)}>
            <option value="">All</option>
            {SECTIONS.map(sec => <option key={sec} value={sec}>{sec}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Status</div>
          <select style={s.select} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
            <option value="open">Open (default)</option>
            <option value="all">All statuses</option>
            {STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Sort by</div>
          <select style={s.select} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
            <option value="date">Date</option>
            <option value="priority">Priority</option>
            <option value="section">Section</option>
            <option value="submitter">Submitter</option>
            <option value="type">Type</option>
            <option value="title">Title (A–Z)</option>
          </select>
        </div>
      </div>

      {/* Body */}
      {loading && <div style={{ padding:'40px', textAlign:'center', color:c.muted }}>Loading…</div>}
      {error && <div style={{ padding:'14px', background:'rgba(239,68,68,0.1)', color:'#ef4444', borderRadius:'8px', marginBottom:'12px' }}>{error}</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ padding:'40px', textAlign:'center', color:c.muted, background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'10px' }}>
          {rows.length === 0 ? 'No submissions yet. Click + New Submission or use the floating Report button.' : 'No submissions match your filters.'}
        </div>
      )}

      {/* Cards */}
      {!loading && filtered.map(row => (
        <SubmissionCard
          key={row.id}
          row={row}
          attachments={attachmentsByFeedback[row.id] || []}
          expanded={expandedId === row.id}
          onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
          isCurator={isCurator}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          c={c}
          s={s}
          isDark={isDark}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubmissionCard
// ---------------------------------------------------------------------------

function SubmissionCard({ row, attachments, expanded, onToggle, isCurator, onStatusChange, onDelete, c, s, isDark }) {
  const priorityRgb = PRIORITY_COLORS[row.priority] || '148,163,184';
  const typeRgb = TYPE_COLORS[row.type] || '148,163,184';
  const statusRgb = STATUS_COLORS[row.status] || '148,163,184';

  const ago = timeAgo(row.created_at);

  return (
    <div style={{
      background: c.card, border:`1px solid ${c.cardBorder}`,
      borderLeft: `3px solid rgb(${priorityRgb})`,
      borderRadius:'8px', padding:'12px 16px', marginBottom:'8px',
      cursor:'pointer',
    }} onClick={onToggle}>

      {/* Top row */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', fontSize:'11px', marginBottom:'4px' }}>
        <span style={s.badge(typeRgb)}>{row.type.toUpperCase()}</span>
        <span style={{ color:`rgb(${priorityRgb})`, fontWeight:'600' }}>
          {row.priority === 'Critical' ? '!!! ' : row.priority === 'High' ? '!! ' : row.priority === 'Medium' ? '! ' : '· '}
          {row.priority}
        </span>
        {row.section && <><span style={{ color:c.muted }}>·</span><span style={{ color:c.sub }}>{row.section}</span></>}
        <span style={s.badge(statusRgb)}>{row.status}</span>
        {attachments.length > 0 && <span style={{ color:c.muted, fontSize:'11px' }}>📎 {attachments.length}</span>}
        <div style={{ flex:1 }} />
        {row.source_url && (
          <a href={row.source_url} onClick={e => e.stopPropagation()} style={{ color:'#60a5fa', fontSize:'11px', textDecoration:'none' }}>↗ Go to source</a>
        )}
      </div>

      {/* Title */}
      <div style={{ fontWeight:'600', fontSize:'14px', color:c.text, marginBottom:'2px' }}>{row.title}</div>

      {/* Submitter line */}
      <div style={{ fontSize:'11px', color:c.muted }}>{row.submitter_name} · {ago}</div>

      {/* Expanded body */}
      {expanded && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop:'14px', paddingTop:'14px', borderTop:`1px solid ${c.cardBorder}` }}>

          {row.description && (
            <div style={{ marginBottom:'12px' }}>
              <div style={s.label}>Description</div>
              <div style={{ fontSize:'13px', color:c.text, whiteSpace:'pre-wrap', lineHeight:1.5 }}>{row.description}</div>
            </div>
          )}

          {row.steps_to_reproduce && (
            <div style={{ marginBottom:'12px' }}>
              <div style={s.label}>Steps to reproduce</div>
              <div style={{ fontSize:'13px', color:c.text, whiteSpace:'pre-wrap', lineHeight:1.5, fontFamily:"'DM Mono',monospace", background: c.rowBg, padding:'10px 12px', borderRadius:'6px' }}>{row.steps_to_reproduce}</div>
            </div>
          )}

          {(row.source_page || row.source_url) && (
            <div style={{ marginBottom:'12px', background: isDark?'rgba(96,165,250,0.08)':'rgba(96,165,250,0.06)', borderRadius:'6px', padding:'10px 12px', fontSize:'12px' }}>
              {row.source_page && <div style={{ marginBottom:'2px' }}><span style={{ color:c.muted, fontWeight:'600' }}>Page:</span> {row.source_page}</div>}
              {row.source_url && <div style={{ wordBreak:'break-all' }}><span style={{ color:c.muted, fontWeight:'600' }}>URL:</span> <a href={row.source_url} style={{ color:'#60a5fa', textDecoration:'none' }}>{row.source_url}</a></div>}
            </div>
          )}

          {attachments.length > 0 && (
            <div style={{ marginBottom:'12px' }}>
              <div style={s.label}>Attachments ({attachments.length})</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'8px' }}>
                {attachments.map(att => <AttachmentTile key={att.id} att={att} c={c} isDark={isDark} />)}
              </div>
            </div>
          )}

          {row.resolution_note && (
            <div style={{ marginBottom:'12px' }}>
              <div style={s.label}>Resolution note</div>
              <div style={{ fontSize:'13px', color:c.text, whiteSpace:'pre-wrap', fontStyle:'italic' }}>{row.resolution_note}</div>
            </div>
          )}

          {/* Curator action row */}
          {isCurator && (
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'14px', paddingTop:'14px', borderTop:`1px solid ${c.cardBorder}`, flexWrap:'wrap' }}>
              <div style={{ fontSize:'11px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:'600' }}>Curator actions</div>
              <select value={row.status} onChange={e => onStatusChange(row, e.target.value)}
                style={{ ...s.select, width:'auto', minWidth:'140px', fontSize:'12px' }}>
                {STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
              <div style={{ flex:1 }} />
              <button onClick={() => onDelete(row)}
                style={{ ...s.btn, background: isDark?'rgba(239,68,68,0.15)':'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:'12px', padding:'6px 12px' }}>
                Delete
              </button>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

function AttachmentTile({ att, c, isDark }) {
  const isImage = (att.mime_type || '').startsWith('image/');
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (isImage) {
      fileUrl(att.storage_path, 'preview')
        .then((u) => { if (!cancelled && u) setThumbUrl(u); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [att.storage_path, isImage]);

  async function openFile() {
    const url = await fileUrl(att.storage_path, 'open').catch(() => null);
    if (url) window.open(url, '_blank', 'noopener');
  }

  const kindBadgeColors = {
    screenshot: '96,165,250',
    sketch:     '167,139,250',
    document:   '52,211,153',
    reference:  '148,163,184',
  };

  return (
    <div onClick={openFile} style={{
      background: c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'8px',
      overflow:'hidden', cursor:'pointer',
    }}>
      <div style={{ height:'90px', background: c.rowBg, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
        {isImage && thumbUrl
          ? <img src={thumbUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <div style={{ fontSize:'32px', opacity:0.5 }}>📄</div>}
      </div>
      <div style={{ padding:'8px 10px' }}>
        <div style={{ fontSize:'11px', fontWeight:'600', color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{att.file_name}</div>
        <div style={{ fontSize:'10px', color:c.muted, marginTop:'2px', display:'flex', alignItems:'center', gap:'6px' }}>
          <span style={{ background:`rgba(${kindBadgeColors[att.kind] || '148,163,184'},0.15)`, color:`rgb(${kindBadgeColors[att.kind] || '148,163,184'})`, padding:'1px 6px', borderRadius:'8px', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em' }}>{att.kind}</span>
          {att.file_size && <span>{formatBytes(att.file_size)}</span>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New submission tab
// ---------------------------------------------------------------------------

function NewSubmissionTab({ c, s, isDark, staffUser, prefill, onSubmitted, onCancel }) {
  const [type, setType] = useState('Bug');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [section, setSection] = useState(prefill?.section || '');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [stepsOpen, setStepsOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // {file, kind, previewUrl}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Hydrate from prefill (Report this page)
  useEffect(() => {
    if (prefill?.section) setSection(prefill.section);
    if (prefill?.screenshotBlob) {
      const file = new File([prefill.screenshotBlob], prefill.screenshotFilename || `screenshot_${Date.now()}.png`, { type:'image/png' });
      const previewUrl = URL.createObjectURL(file);
      setPendingFiles([{ file, kind:'screenshot', previewUrl }]);
    }
  }, [prefill]);

  function detectKind(file) {
    const n = (file.name || '').toLowerCase();
    if (n.includes('sketch') || n.includes('mockup') || n.includes('wireframe')) return 'sketch';
    if (file.type.startsWith('image/')) return 'screenshot';
    if (n.endsWith('.pdf') || n.endsWith('.docx') || n.endsWith('.doc') || n.endsWith('.txt') || n.endsWith('.md')) return 'document';
    return 'reference';
  }

  function handleFileSelect(files) {
    const arr = Array.from(files || []);
    const next = arr.map(file => ({
      file,
      kind: detectKind(file),
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setPendingFiles(prev => [...prev, ...next]);
  }

  function removeFile(idx) {
    setPendingFiles(prev => {
      const f = prev[idx];
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  }

  function setKind(idx, kind) {
    setPendingFiles(prev => prev.map((f, i) => i === idx ? { ...f, kind } : f));
  }

  async function handleSubmit() {
    if (!title.trim()) { setError('Title is required.'); return; }
    setSubmitting(true);
    setError('');

    try {
      // 1. Insert feedback row
      const { data: inserted, error: e1 } = await supabase
        .from('wigley_feedback')
        .insert({
          type,
          priority,
          status: 'Inbox',
          section: section || null,
          title: title.trim(),
          description: description.trim() || null,
          steps_to_reproduce: steps.trim() || null,
          source_url: prefill?.url || null,
          source_page: prefill?.page || null,
          submitter_id: staffUser?.id || null,
          submitter_name: staffUser?.name || 'Unknown',
        })
        .select()
        .single();
      if (e1) throw e1;

      // 2. Upload files + insert attachment rows
      for (const pf of pendingFiles) {
        const sanitized = (pf.file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
        const path = `feedback-attachments/${inserted.id}/${crypto.randomUUID()}_${sanitized}`;
        const { error: upErr } = await uploadFile(path, pf.file);
        if (upErr) throw upErr;
        const { error: attErr } = await supabase.from('wigley_feedback_attachments').insert({
          feedback_id: inserted.id,
          file_name: pf.file.name,
          file_size: pf.file.size,
          mime_type: pf.file.type || null,
          storage_path: path,
          kind: pf.kind,
          uploaded_by_id: staffUser?.id || null,
          uploaded_by_name: staffUser?.name || 'Unknown',
        });
        if (attErr) throw attErr;
      }

      // 3. Cleanup previews + done
      pendingFiles.forEach(pf => pf.previewUrl && URL.revokeObjectURL(pf.previewUrl));
      onSubmitted();
    } catch (e) {
      setError(e.message || String(e));
      setSubmitting(false);
    }
  }

  return (
    <div style={{ background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'10px', padding:'18px 22px' }}>

      {/* Pre-fill summary box (only when reporting from a page) */}
      {prefill && (
        <div style={{ background:isDark?'rgba(96,165,250,0.08)':'rgba(96,165,250,0.06)', borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'12px' }}>
          <div style={{ fontSize:'10px', fontWeight:'700', color:'#60a5fa', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>Auto-captured from current page</div>
          {prefill.page && <div><span style={{ color:c.muted, fontWeight:'600', display:'inline-block', width:'80px' }}>Page</span> {prefill.page}</div>}
          {prefill.url && <div style={{ wordBreak:'break-all' }}><span style={{ color:c.muted, fontWeight:'600', display:'inline-block', width:'80px' }}>URL</span> <span style={{ fontFamily:"'DM Mono',monospace" }}>{prefill.url}</span></div>}
          <div><span style={{ color:c.muted, fontWeight:'600', display:'inline-block', width:'80px' }}>Submitter</span> {staffUser?.name || 'Unknown'}</div>
        </div>
      )}

      {/* Type buttons */}
      <div style={{ marginBottom:'14px' }}>
        <div style={s.label}>Type</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'8px' }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setType(t)}
              style={{
                padding:'10px', borderRadius:'7px', cursor:'pointer', fontSize:'13px', fontWeight:'600',
                background: type === t ? `rgba(${TYPE_COLORS[t]},0.15)` : c.inputBg,
                color: type === t ? `rgb(${TYPE_COLORS[t]})` : c.text,
                border: type === t ? `2px solid rgb(${TYPE_COLORS[t]})` : `1px solid ${c.inputBorder}`,
              }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom:'14px' }}>
        <div style={s.label}>Title <span style={{ color:'#ef4444' }}>*</span></div>
        <input style={s.input} value={title} onChange={e=>setTitle(e.target.value)}
          placeholder={type === 'Bug' ? 'e.g. Login fails with 500 when email has uppercase' : 'e.g. Speed up initial dashboard load'} />
      </div>

      {/* Priority + Section row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'14px' }}>
        <div>
          <div style={s.label}>Priority</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'4px' }}>
            {PRIORITIES.map(p => (
              <button key={p} onClick={() => setPriority(p)}
                style={{
                  padding:'7px', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600',
                  background: priority === p ? `rgba(${PRIORITY_COLORS[p]},0.15)` : c.inputBg,
                  color: priority === p ? `rgb(${PRIORITY_COLORS[p]})` : c.text,
                  border: priority === p ? `2px solid rgb(${PRIORITY_COLORS[p]})` : `1px solid ${c.inputBorder}`,
                }}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={s.label}>Section</div>
          <select style={s.select} value={section} onChange={e=>setSection(e.target.value)}>
            <option value="">— Select a section —</option>
            {SECTIONS.map(sec => <option key={sec} value={sec}>{sec}</option>)}
          </select>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom:'14px' }}>
        <div style={s.label}>Description</div>
        <textarea style={s.textarea} value={description} onChange={e=>setDescription(e.target.value)}
          placeholder="What's the issue or idea? Be specific so future-you doesn't have to guess." />
      </div>

      {/* Steps to reproduce (collapsible) */}
      <div style={{ marginBottom:'14px' }}>
        <div onClick={() => setStepsOpen(!stepsOpen)} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>
          <span>{stepsOpen ? '▾' : '▸'}</span>
          Steps to reproduce <span style={{ fontWeight:'400', textTransform:'none', letterSpacing:0, color:c.muted }}>(optional)</span>
        </div>
        {stepsOpen && (
          <textarea style={{ ...s.textarea, fontFamily:"'DM Mono',monospace", fontSize:'12px' }} value={steps} onChange={e=>setSteps(e.target.value)}
            placeholder={'1. Go to...\n2. Click on...\n3. See error'} />
        )}
      </div>

      {/* Attachments */}
      <div style={{ marginBottom:'14px' }}>
        <div style={s.label}>Attachments</div>
        <FileDropzone onFiles={handleFileSelect} c={c} isDark={isDark} />
        {pendingFiles.length > 0 && (
          <div style={{ marginTop:'10px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'8px' }}>
            {pendingFiles.map((pf, i) => (
              <div key={i} style={{ background:c.rowBg, border:`1px solid ${c.cardBorder}`, borderRadius:'8px', padding:'8px' }}>
                <div style={{ height:'80px', display:'flex', alignItems:'center', justifyContent:'center', background:c.card, borderRadius:'6px', marginBottom:'6px', overflow:'hidden' }}>
                  {pf.previewUrl
                    ? <img src={pf.previewUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <div style={{ fontSize:'28px', opacity:0.5 }}>📄</div>}
                </div>
                <div style={{ fontSize:'11px', fontWeight:'600', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'4px' }}>{pf.file.name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                  <select value={pf.kind} onChange={e=>setKind(i, e.target.value)} style={{ flex:1, padding:'3px 5px', fontSize:'10px', borderRadius:'4px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text }}>
                    <option value="screenshot">screenshot</option>
                    <option value="sketch">sketch</option>
                    <option value="document">document</option>
                    <option value="reference">reference</option>
                  </select>
                  <button onClick={()=>removeFile(i)} style={{ background:'transparent', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'14px' }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ padding:'10px 12px', background:'rgba(239,68,68,0.1)', color:'#ef4444', borderRadius:'6px', marginBottom:'10px', fontSize:'12px' }}>{error}</div>}

      {/* Actions */}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', paddingTop:'10px', borderTop:`1px solid ${c.cardBorder}` }}>
        <button onClick={onCancel} disabled={submitting} style={{ ...s.btn, ...s.btnGhost(isDark) }}>Cancel</button>
        <button onClick={handleSubmit} disabled={submitting || !title.trim()} style={{ ...s.btn, ...s.btnPrimary, opacity: (submitting || !title.trim()) ? 0.5 : 1, cursor: (submitting || !title.trim()) ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

function FileDropzone({ onFiles, c, isDark }) {
  const [over, setOver] = useState(false);
  const inputRef = React.useRef(null);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      style={{
        padding:'18px', textAlign:'center', cursor:'pointer',
        border:`2px dashed ${over ? c.gold : c.cardBorder}`, borderRadius:'8px',
        background: over ? (isDark?'rgba(180,0,0,0.06)':'rgba(180,0,0,0.04)') : c.rowBg,
        fontSize:'12px', color:c.muted,
      }}>
      <input ref={inputRef} type="file" multiple style={{ display:'none' }} onChange={e=>onFiles(e.target.files)} />
      <div>Drag &amp; drop sketches, mockups, screenshots, or specs here, or <span style={{ color:c.gold, fontWeight:'600' }}>click to browse</span></div>
      <div style={{ fontSize:'11px', marginTop:'4px' }}>Any file type. Multiple files OK.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roadmap tab
// ---------------------------------------------------------------------------

function RoadmapTab({ c, s, isDark, isCurator, staffUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  // Add form state
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newStatus, setNewStatus] = useState('Coming Soon');
  const [adding, setAdding] = useState(false);

  // Inline-edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: e1 } = await supabase
        .from('wigley_feedback_roadmap')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (e1) throw e1;
      setItems(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const out = { all: items.length, Complete:0, 'In Development':0, 'Coming Soon':0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const filtered = useMemo(() => filter === 'all' ? items : items.filter(i => i.status === filter), [items, filter]);

  async function handleAdd() {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const maxOrder = items.reduce((m, i) => Math.max(m, i.display_order || 0), 0);
      const { data, error: e1 } = await supabase
        .from('wigley_feedback_roadmap')
        .insert({
          title: newTitle.trim(),
          description: newDesc.trim() || null,
          status: newStatus,
          display_order: maxOrder + 10,
          created_by_id: staffUser?.id || null,
          created_by_name: staffUser?.name || 'Unknown',
        })
        .select()
        .single();
      if (e1) throw e1;
      setItems(prev => [...prev, data]);
      setNewTitle('');
      setNewDesc('');
      setNewStatus('Coming Soon');
    } catch (e) {
      alert('Add failed: ' + (e.message || e));
    } finally {
      setAdding(false);
    }
  }

  async function handleStatusChange(item, status) {
    try {
      const { error: e1 } = await supabase
        .from('wigley_feedback_roadmap')
        .update({ status, updated_by_id: staffUser?.id || null, updated_by_name: staffUser?.name || 'Unknown' })
        .eq('id', item.id);
      if (e1) throw e1;
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status, completed_at: status === 'Complete' ? new Date().toISOString() : null } : i));
    } catch (e) {
      alert('Status change failed: ' + (e.message || e));
    }
  }

  async function handleSaveEdit(item) {
    try {
      const update = {
        title: (editDraft.title ?? item.title).trim(),
        description: (editDraft.description ?? item.description) || null,
        updated_by_id: staffUser?.id || null,
        updated_by_name: staffUser?.name || 'Unknown',
      };
      const { error: e1 } = await supabase.from('wigley_feedback_roadmap').update(update).eq('id', item.id);
      if (e1) throw e1;
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...update } : i));
      setEditingId(null);
      setEditDraft({});
    } catch (e) {
      alert('Save failed: ' + (e.message || e));
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.title}" from the roadmap?`)) return;
    try {
      const { error: e1 } = await supabase.from('wigley_feedback_roadmap').delete().eq('id', item.id);
      if (e1) throw e1;
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      alert('Delete failed: ' + (e.message || e));
    }
  }

  async function handleMove(item, direction) {
    const idx = items.findIndex(i => i.id === item.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];

    try {
      // Swap display_order
      await Promise.all([
        supabase.from('wigley_feedback_roadmap').update({ display_order: target.display_order }).eq('id', item.id),
        supabase.from('wigley_feedback_roadmap').update({ display_order: item.display_order }).eq('id', target.id),
      ]);
      setItems(prev => {
        const copy = prev.slice();
        copy[idx] = { ...item, display_order: target.display_order };
        copy[targetIdx] = { ...target, display_order: item.display_order };
        return copy.sort((a, b) => (a.display_order||0) - (b.display_order||0));
      });
    } catch (e) {
      alert('Reorder failed: ' + (e.message || e));
    }
  }

  return (
    <div>
      <div style={{ marginBottom:'12px' }}>
        <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'18px', fontWeight:'600', color:c.text, marginBottom:'4px' }}>Feature Roadmap</div>
        <div style={{ fontSize:'12px', color:c.muted }}>What's shipped, what's in flight, and what's next. {isCurator ? 'You can add, edit, reorder, and update statuses.' : 'Curated by Steph and Philip.'}</div>
      </div>

      <div style={{ display:'flex', gap:'6px', marginBottom:'14px' }}>
        <button onClick={()=>setFilter('all')} style={s.pill('148,163,184', filter==='all')}>all ({counts.all})</button>
        {ROADMAP_STATUSES.map(st => (
          <button key={st} onClick={()=>setFilter(st)} style={s.pill(ROADMAP_STATUS_COLORS[st], filter===st)}>● {st.toLowerCase()} ({counts[st]||0})</button>
        ))}
      </div>

      {/* Curator add form */}
      {isCurator && (
        <div style={{ background: isDark?'rgba(249,115,22,0.06)':'rgba(249,115,22,0.04)', border:`1px dashed rgba(249,115,22,0.3)`, borderRadius:'10px', padding:'12px 14px', marginBottom:'14px' }}>
          <div style={{ fontSize:'10px', fontWeight:'700', color:'#f97316', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px' }}>+ Add new feature · curator-only</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 160px', gap:'8px', marginBottom:'8px' }}>
            <input style={s.input} placeholder="New feature title…" value={newTitle} onChange={e=>setNewTitle(e.target.value)} />
            <select style={s.select} value={newStatus} onChange={e=>setNewStatus(e.target.value)}>
              {ROADMAP_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <textarea style={s.textarea} placeholder="Description (optional — multi-line OK)" value={newDesc} onChange={e=>setNewDesc(e.target.value)} />
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'8px' }}>
            <button onClick={handleAdd} disabled={adding || !newTitle.trim()} style={{ ...s.btn, ...s.btnPrimary, opacity:(adding||!newTitle.trim())?0.5:1 }}>{adding ? 'Adding…' : '+ Add feature'}</button>
          </div>
        </div>
      )}

      {/* Items */}
      {loading && <div style={{ padding:'40px', textAlign:'center', color:c.muted }}>Loading…</div>}
      {error && <div style={{ padding:'14px', background:'rgba(239,68,68,0.1)', color:'#ef4444', borderRadius:'8px', marginBottom:'12px' }}>{error}</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ padding:'40px', textAlign:'center', color:c.muted, background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'10px' }}>
          {items.length === 0 ? 'No roadmap items yet.' : 'No items match this filter.'}
        </div>
      )}

      {!loading && filtered.map((item, idx) => {
        const editing = editingId === item.id;
        return (
          <div key={item.id} style={{ background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'8px', padding:'12px 14px', marginBottom:'8px', display:'grid', gridTemplateColumns: isCurator ? '16px 1fr 150px 100px' : '1fr 150px', gap:'12px', alignItems:'start' }}>
            {isCurator && <div style={{ color:c.muted, fontSize:'14px', cursor:'grab', paddingTop:'2px' }}>⋮⋮</div>}
            <div>
              {editing ? (
                <>
                  <input style={{ ...s.input, marginBottom:'6px', fontWeight:'600' }} value={editDraft.title ?? item.title} onChange={e=>setEditDraft(d=>({...d, title:e.target.value}))} />
                  <textarea style={{ ...s.textarea, minHeight:'70px' }} value={editDraft.description ?? (item.description||'')} onChange={e=>setEditDraft(d=>({...d, description:e.target.value}))} />
                </>
              ) : (
                <>
                  <div style={{ fontWeight:'600', fontSize:'14px', color:c.text, marginBottom:'2px' }}>{item.title}</div>
                  {item.description && <div style={{ fontSize:'12px', color:c.sub, whiteSpace:'pre-wrap', lineHeight:1.5 }}>{item.description}</div>}
                  {item.completed_at && <div style={{ fontSize:'11px', color:c.muted, marginTop:'4px' }}>Completed {new Date(item.completed_at).toLocaleDateString()}</div>}
                </>
              )}
            </div>
            <div>
              {isCurator && !editing ? (
                <select value={item.status} onChange={e=>handleStatusChange(item, e.target.value)} style={{ ...s.select, fontSize:'11px' }}>
                  {ROADMAP_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              ) : (
                <span style={s.badge(ROADMAP_STATUS_COLORS[item.status])}>● {item.status}</span>
              )}
            </div>
            {isCurator && (
              <div style={{ display:'flex', gap:'4px', justifyContent:'flex-end', flexWrap:'wrap' }}>
                {editing ? (
                  <>
                    <button onClick={()=>handleSaveEdit(item)} style={{ ...s.btn, padding:'4px 8px', fontSize:'11px', background:'rgba(52,211,153,0.15)', color:'#10b981' }}>Save</button>
                    <button onClick={()=>{ setEditingId(null); setEditDraft({}); }} style={{ ...s.btn, padding:'4px 8px', fontSize:'11px', ...s.btnGhost(isDark) }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={()=>handleMove(item, 'up')}   disabled={idx===0} style={{ ...s.btn, padding:'4px 8px', fontSize:'12px', ...s.btnGhost(isDark), opacity: idx===0 ? 0.3 : 1 }}>↑</button>
                    <button onClick={()=>handleMove(item, 'down')} disabled={idx===filtered.length-1} style={{ ...s.btn, padding:'4px 8px', fontSize:'12px', ...s.btnGhost(isDark), opacity: idx===filtered.length-1 ? 0.3 : 1 }}>↓</button>
                    <button onClick={()=>{ setEditingId(item.id); setEditDraft({ title:item.title, description:item.description||'' }); }} style={{ ...s.btn, padding:'4px 8px', fontSize:'12px', ...s.btnGhost(isDark) }}>✎</button>
                    <button onClick={()=>handleDelete(item)} style={{ ...s.btn, padding:'4px 8px', fontSize:'12px', background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>✕</button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024*1024) return `${(n/1024).toFixed(1)} KB`;
  return `${(n/(1024*1024)).toFixed(1)} MB`;
}
